/**
 * Table showing a skipper's uploaded track files with status badges.
 * Status derived from parseStatus + race assignment.
 */

import type { RawTrack } from '@regatta-replay/types';
import { formatFileSize } from '@/lib/upload';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface SkipperUploadTableProps {
  uploads: RawTrack[];
  raceDates: string[];  // YYYY-MM-DD dates that have races
}

function getFileName(s3Key: string): string {
  return s3Key.split('/').pop() || s3Key;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface StatusBadge {
  label: string;
  icon: string;
  className: string;
  tooltip?: string;
}

function getStatusBadge(track: RawTrack, raceDates: string[]): StatusBadge {
  if (track.parseStatus === 'error') {
    return {
      label: 'Error',
      icon: '❌',
      className: 'text-red-600 bg-red-50',
      tooltip: track.parseError || 'Parse failed',
    };
  }

  if (track.parseStatus === 'processing' || track.parseStatus === 'pending') {
    return {
      label: 'Processing',
      icon: '⏳',
      className: 'text-yellow-700 bg-yellow-50',
    };
  }

  if (track.parseStatus === 'matching') {
    return {
      label: 'Matching',
      icon: '⏳',
      className: 'text-blue-700 bg-blue-50',
      tooltip: 'Finding matching race...',
    };
  }

  if (track.parseStatus === 'matched') {
    const matchName = track.matchCandidates?.[0]?.raceName;
    return {
      label: 'Matched',
      icon: '✅',
      className: 'text-green-700 bg-green-50',
      tooltip: matchName ? `Matched to ${matchName}` : undefined,
    };
  }

  if (track.parseStatus === 'ambiguous') {
    const count = track.matchCandidates?.length || 0;
    return {
      label: 'Review',
      icon: '⚠️',
      className: 'text-yellow-700 bg-yellow-50',
      tooltip: `${count} possible race match${count !== 1 ? 'es' : ''} — review needed`,
    };
  }

  if (track.parseStatus === 'unmatched') {
    return {
      label: 'No match',
      icon: '—',
      className: 'text-gray-500 bg-gray-50',
      tooltip: 'No matching race found nearby. Assign manually.',
    };
  }

  // parsed — check if track date matches a race date
  const hasMatchingRace = track.trackDate ? raceDates.includes(track.trackDate) : false;

  if (hasMatchingRace) {
    return {
      label: 'Ready',
      icon: '✅',
      className: 'text-green-700 bg-green-50',
    };
  }

  return {
    label: 'No race',
    icon: '⚠️',
    className: 'text-yellow-700 bg-yellow-50',
    tooltip: track.trackDate ? `No race found for ${formatDate(track.trackDate)}.` : 'Track date not yet determined.',
  };
}

export function SkipperUploadTable({ uploads, raceDates }: SkipperUploadTableProps) {
  if (uploads.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-8 text-center" data-testid="no-uploads">
        <p className="text-sm text-muted-foreground">
          No tracks uploaded yet. Use the dropzone above to upload your track files.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white" data-testid="upload-table">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-navy-900">Your Uploads</h3>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>File</TableHead>
            <TableHead>Date</TableHead>
            <TableHead>Size</TableHead>
            <TableHead className="text-right">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {uploads.map((track) => {
            const badge = getStatusBadge(track, raceDates);
            return (
              <TableRow key={track.rawTrackId}>
                <TableCell className="font-medium text-navy-900">
                  {getFileName(track.s3Key)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {track.trackDate ? formatDate(track.trackDate) : '—'}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatFileSize(track.fileSize)}
                </TableCell>
                <TableCell className="text-right">
                  {badge.tooltip ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                            data-testid={`status-${track.rawTrackId}`}
                          >
                            {badge.icon} {badge.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{badge.tooltip}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
                      data-testid={`status-${track.rawTrackId}`}
                    >
                      {badge.icon} {badge.label}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
